import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";
import type { RootState } from "@/store/store";
import { reusableFetch, reusableFormDataFetch } from "@/utils/useFetch";
import { ITemplatesState } from "./types/templatesSlice";

interface GetTemplatesData {
  limit: number;
  cursor: string;
  search?: string;
  messageType?: string;
}

interface GetTemplatesByIdData {
  templateId: string;
  templateVersionId?: string;
}

interface GetDefaultTemplateData {
  messageType: string;
  templateType: string;
}

interface UpdateTemplateData {
  templateId: string;
  messageType: string;
  name: string;
  subject: string;
  templateType: string;
  variables: string[];
  body: string;
}

interface GetDocTemplatesFromTemplateTypeData {
  customerId: string;
  applicationId: string;
  userId: string;
  templateType: string;
  jwt?: string;
}

export const getTemplates = createAsyncThunk("templates/list", async (data: GetTemplatesData, { rejectWithValue }) => {
  const templates = await reusableFetch(
    `template/all?limit=${data.limit}&cursor=${data.cursor}&search=${data.search ?? ""}&messageType=${data.messageType ?? ""}`,
    "GET",
    {},
    rejectWithValue,
  );
  return templates;
});

export const getTemplatesById = createAsyncThunk(
  "templates/getTemplatesById",
  async (data: GetTemplatesByIdData, { rejectWithValue }) => {
    let url = `template/${data.templateId}`;

    if (data?.templateVersionId) {
      url = url + `?templateVersionId=${data.templateVersionId}`;
    }

    const template = await reusableFetch(url, "GET", {}, rejectWithValue);

    return template;
  },
);

export const getTemplatesVersions = createAsyncThunk(
  "templates/getTemplatesVersions",
  async (data: GetTemplatesByIdData, { rejectWithValue }) => {
    const versions = await reusableFetch(`template/versions/${data.templateId}`, "GET", {}, rejectWithValue);
    return versions;
  },
);

export const getDefaultTemplate = createAsyncThunk(
  "templates/getDefaultTemplate",
  async (data: GetDefaultTemplateData, { rejectWithValue }) => {
    const defaultTemplate = await reusableFetch(
      `template/default/${data.messageType}/${data.templateType}`,
      "GET",
      {},
      rejectWithValue,
    );
    return defaultTemplate;
  },
);

export const updateTemplate = createAsyncThunk(
  "templates/updateTemplate",
  async (data: UpdateTemplateData, { rejectWithValue }) => {
    const { templateId, messageType, ...rest } = data;
    const updatedTemplate = await reusableFetch(`template/${messageType}/${templateId}`, "PUT", rest, rejectWithValue);
    return updatedTemplate;
  },
);

export const saveDocumentTemplate = createAsyncThunk(
  "templates/saveDocumentTemplate",
  async ({ formData }: { formData: FormData }, { rejectWithValue }) => {
    const documentTemplate = await reusableFormDataFetch(
      `template/upload/document/${formData.get("templateType")}`,
      "POST",
      formData,
      rejectWithValue,
      false,
    );
    return documentTemplate;
  },
);

export const getDocTemplatesFromTemplateType = createAsyncThunk(
  "templates/getDocTemplatesFromTemplateType",
  async (data: GetDocTemplatesFromTemplateTypeData, { rejectWithValue }) => {
    const { jwt, ...rest } = data;
    const docTemplate = await reusableFetch(
      `template/document/${data.templateType}`,
      "POST",
      rest,
      rejectWithValue,
      false,
      jwt,
    );
    return docTemplate;
  },
);

export const previewDocument = createAsyncThunk(
  "templates/previewDocument",
  async (data: { templateId: string }, { rejectWithValue }) => {
    const previewDocument = await reusableFetch(`template/preview/${data.templateId}`, "GET", {}, rejectWithValue);
    return previewDocument;
  },
);

export const getSampleDocument = createAsyncThunk(
  "templates/getSampleDocument",
  async (data: { templateType: string }, { rejectWithValue }) => {
    const sampleDocument = await reusableFetch(`template/sample/${data.templateType}`, "GET", {}, rejectWithValue);
    return sampleDocument;
  },
);

const initialState: ITemplatesState = {
  templates: [],
  templateVersions: [],
  isSavingTemplates: false,
  isFetchingTemplates: false,
  isFetchingVersion: false,
  isFetchingDocument: false,
  isFetchingPreviewDocument: false,
  isFetchingSampleDocument: false,
  document: {
    template: undefined,
    status: undefined,
  },
  currentlyEditingTemplate: {
    name: "",
    subject: "",
    messageType: "",
    templateType: "",
    variables: [],
    body: "",
    receiverEmails: [],
    isReceiverEmailsRequired: false,
  },
  allowedVariables: {},
  allowCalculatedFields: {},
  isFetchingTemplate: false,
};

export const templatesSlice = createSlice({
  name: "templates",
  initialState: initialState,
  reducers: {
    saveValue: (state, action) => _.merge(state, action.payload),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTemplates.fulfilled, (state, { payload }) => {
        // @ts-expect-error: ignoring as this is existing code
        state.templates = payload.data.data.getAllTemplateData.docs;
        state.isFetchingTemplates = false;
      })
      .addCase(getTemplates.pending, (state) => {
        state.isFetchingTemplates = true;
      })
      .addCase(getTemplates.rejected, (state) => {
        state.templates = [];
        state.isFetchingTemplates = false;
      })
      .addCase(updateTemplate.fulfilled, (state, { payload }) => {
        state.isSavingTemplates = false;
      })
      .addCase(updateTemplate.pending, (state) => {
        state.isSavingTemplates = true;
      })
      .addCase(updateTemplate.rejected, (state) => {
        state.isSavingTemplates = false;
      })
      .addCase(getTemplatesById.pending, (state) => {
        state.allowedVariables = {};
        state.allowCalculatedFields = {};
        state.currentlyEditingTemplate = initialState.currentlyEditingTemplate;
        state.isFetchingTemplate = true;
      })
      .addCase(getTemplatesById.fulfilled, (state, { payload }) => {
        // @ts-expect-error: ignoring as this is existing code
        state.currentlyEditingTemplate = payload.data.data?.template;
        // @ts-expect-error: ignoring as this is existing code
        state.allowedVariables = payload.data.data?.allowedVariables ?? {};
        // @ts-expect-error: ignoring as this is existing code
        state.allowCalculatedFields = payload.data.data?.allowCalculatedFields ?? {};
        state.isFetchingTemplate = false;
      })
      .addCase(getTemplatesById.rejected, (state) => {
        state.allowedVariables = {};
        state.allowCalculatedFields = {};
        state.currentlyEditingTemplate = initialState.currentlyEditingTemplate;
        state.isFetchingTemplate = false;
      })
      .addCase(getDefaultTemplate.pending, (state) => {
        state.isFetchingTemplate = true;
      })
      .addCase(getDefaultTemplate.fulfilled, (state, { payload }) => {
        // @ts-expect-error: ignoring as this is existing code
        state.currentlyEditingTemplate.body = payload.data?.data;
        state.isFetchingTemplate = false;
      })
      .addCase(getDefaultTemplate.rejected, (state) => {
        state.isFetchingTemplate = false;
      })
      .addCase(saveDocumentTemplate.pending, (state) => {
        state.isFetchingDocument = true;
      })
      .addCase(saveDocumentTemplate.fulfilled, (state, { payload }) => {
        state.isFetchingDocument = false;
        // @ts-expect-error: ignoring as this is existing code
        state.currentlyEditingTemplate["body"] = payload.data?.data?.body;
      })
      .addCase(saveDocumentTemplate.rejected, (state) => {
        state.isFetchingDocument = false;
      })
      .addCase(getTemplatesVersions.pending, (state) => {
        state.templateVersions = [];
        state.isFetchingVersion = true;
      })
      .addCase(getTemplatesVersions.fulfilled, (state, { payload }) => {
        // @ts-expect-error: ignoring as this is existing code
        state.templateVersions = payload.data.data;
        state.isFetchingVersion = false;
      })
      .addCase(getTemplatesVersions.rejected, (state) => {
        state.templateVersions = [];
        state.isFetchingVersion = false;
      })
      .addCase(getDocTemplatesFromTemplateType.pending, (state) => {
        state.document = {
          template: undefined,
          status: "pending",
        };
      })
      .addCase(getDocTemplatesFromTemplateType.fulfilled, (state, { payload }) => {
        state.document = {
          // @ts-expect-error: ignoring as this is existing code
          template: payload?.data?.data?.template,
          status: "fulfilled",
        };
      })
      .addCase(getDocTemplatesFromTemplateType.rejected, (state) => {
        state.document = {
          template: undefined,
          status: "rejected",
        };
      })
      .addCase(previewDocument.pending, (state) => {
        state.isFetchingPreviewDocument = true;
      })
      .addCase(previewDocument.fulfilled, (state) => {
        state.isFetchingPreviewDocument = false;
      })
      .addCase(previewDocument.rejected, (state) => {
        state.isFetchingPreviewDocument = false;
      })
      .addCase(getSampleDocument.pending, (state) => {
        state.isFetchingSampleDocument = true;
      })
      .addCase(getSampleDocument.fulfilled, (state) => {
        state.isFetchingSampleDocument = false;
      })
      .addCase(getSampleDocument.rejected, (state) => {
        state.isFetchingSampleDocument = false;
      });
  },
});

export const { saveValue } = templatesSlice.actions;

export const templatesSelector = (state: RootState) => {
  return state.templates;
};
